import {
    decodeInlineComponentsToSegments,
    resolveEntityRefFromPayload,
} from '../services/inlineComponents/decodeInlineComponentStream';
import {isLinkedEntityRef} from '../../../utils/entityLinks';

function normalizeRefKey(ref) {
    return String(ref || '').trim().toLowerCase();
}

function addLinkedRef(refs, seen, raw) {
    const ref = String(raw || '').trim();
    if (!ref || !isLinkedEntityRef(ref)) {
        return;
    }
    const key = normalizeRefKey(ref);
    if (seen.has(key)) {
        return;
    }
    seen.add(key);
    refs.push(ref);
}

function addArrowId(ids, seen, raw) {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) {
        return;
    }
    seen.add(id);
    ids.push(id);
}

function refsMatch(left, right) {
    return normalizeRefKey(left) === normalizeRefKey(right) && Boolean(normalizeRefKey(left));
}

/**
 * Resolve an on-chart arrow from a mutation's from/to pair using the live connections snapshot.
 * Edges are undirected (same as collectChartConnections).
 *
 * @param {Array<{from?: string, to?: string, arrowShapeId?: string}>} connections
 * @param {string} fromRef
 * @param {string} toRef
 * @returns {string|null}
 */
function findConnectionArrowShapeId(connections, fromRef, toRef) {
    if (!fromRef || !toRef || !Array.isArray(connections)) {
        return null;
    }
    const match = connections.find((edge) => (
        (refsMatch(edge?.from, fromRef) && refsMatch(edge?.to, toRef))
        || (refsMatch(edge?.from, toRef) && refsMatch(edge?.to, fromRef))
    ));
    return match?.arrowShapeId || null;
}

/**
 * Collect canvas autoselect targets from a completed Chart Assistant turn:
 * `[[entity:]]` / `[[kb:]]` chips plus mutation entityRef / from-to / arrowShapeId.
 *
 * @param {{
 *   answerText?: string,
 *   mutations?: Array<object>|null,
 *   chartConnections?: Array<object>|null,
 * }} [input]
 * @returns {{entityRefs: string[], arrowShapeIds: string[]}}
 */
export function collectChartAutoselectTargets({
    answerText = '',
    mutations = null,
    chartConnections = null,
} = {}) {
    const entityRefs = [];
    const arrowShapeIds = [];
    const seenRefs = new Set();
    const seenArrows = new Set();

    if (answerText && String(answerText).includes('[[')) {
        const {segments} = decodeInlineComponentsToSegments(String(answerText));
        for (const segment of segments) {
            if (segment?.type !== 'entity' && segment?.type !== 'kb') {
                continue;
            }
            addLinkedRef(entityRefs, seenRefs, resolveEntityRefFromPayload(segment.payload));
        }
    }

    const mutationList = Array.isArray(mutations) ? mutations : [];
    for (const mutation of mutationList) {
        if (!mutation || typeof mutation !== 'object') {
            continue;
        }
        addLinkedRef(entityRefs, seenRefs, mutation.entityRef);
        addLinkedRef(entityRefs, seenRefs, mutation.fromEntityRef);
        addLinkedRef(entityRefs, seenRefs, mutation.toEntityRef);
        addArrowId(arrowShapeIds, seenArrows, mutation.arrowShapeId);
        if (!mutation.arrowShapeId && mutation.fromEntityRef && mutation.toEntityRef) {
            addArrowId(
                arrowShapeIds,
                seenArrows,
                findConnectionArrowShapeId(
                    chartConnections,
                    mutation.fromEntityRef,
                    mutation.toEntityRef
                )
            );
        }
    }

    return {entityRefs, arrowShapeIds};
}

export default collectChartAutoselectTargets;
